import PropTypes from "prop-types"
import { compact } from "lodash"
import { Box, Chip, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { InfoOutlined, Refresh } from "@mui/icons-material"
import moment from "moment"
import GroupingMenu from "components/grouping-menu"
import ExpansionMenu from "components/expansion-menu"
import ListItemDropDown from "components/list-item-dropdown"

function formatDateRangeLabel(startDate, endDate) {
  return `${moment(startDate).format("MMM D, YYYY")} – ${moment(endDate).format("MMM D, YYYY")}`
}

function FrozenContextTooltip({ frozenSnapshot, selectedFactor, selectedCategory }) {
  if (!frozenSnapshot) return null

  const fields = compact([
    frozenSnapshot.modelId && `Model: ${frozenSnapshot.modelId}`,
    selectedFactor && `Factor: ${selectedFactor}`,
    selectedCategory && `Category: ${selectedCategory}`,
    frozenSnapshot.isIntradayMode && "Mode: Intraday",
  ])

  if (!fields.length) return null

  return (
    <Tooltip
      title={(
        <Stack spacing={0.5}>
          {fields.map(field => (
            <Typography key={field} variant="caption">{field}</Typography>
          ))}
        </Stack>
      )}
      placement="bottom-start"
    >
      <InfoOutlined sx={{ fontSize: 16, opacity: 0.6, cursor: "default", ml: 0.5, flexShrink: 0 }} />
    </Tooltip>
  )
}

FrozenContextTooltip.propTypes = {
  frozenSnapshot: PropTypes.object,
  selectedFactor: PropTypes.string,
  selectedCategory: PropTypes.string,
}

export default function DrilldownToolbar({
  viewLabel,
  currentFactor,
  factorOptions,
  onFactorChange,
  frozenSnapshot,
  selectedFactor,
  selectedCategory,
  isIntraday,
  intradayAsOf,
  hasNewerBatch,
  intradayContributorsUnavailable,
  onRefresh,
  groupBy,
  onGroupByChange,
  availableGroupByOptions,
  isExpanded,
  onExpansionToggle,
}) {
  const dateLabel = (() => {
    if (!frozenSnapshot) return "—"
    const { startDate, endDate } = frozenSnapshot
    const formattedEnd = endDate ? moment(endDate).format("MMM D, YYYY") : "—"
    // Single-day intraday view: the "As of {time}" label on the left carries the time-of-day.
    if (isIntraday) {
      return formattedEnd
    }
    // Only performance spans a date range; all other views are point-in-time.
    if (viewLabel === "Performance" && startDate && startDate !== endDate) {
      return `Date Range: ${formatDateRangeLabel(startDate, endDate)}`
    }
    return `on ${formattedEnd}`
  })()

  // Show a factor picker when the drilldown is scoped to a specific factor so
  // the user can jump to a sibling factor in the same category instantly.
  const viewSelector = factorOptions?.length > 1 && onFactorChange
    ? (
      <ListItemDropDown
        label=""
        value={currentFactor}
        setValue={onFactorChange}
        options={factorOptions}
        alignMenu="left"
        classes={{ toggle: "contributors-datagrid-drilldown-view-selector" }}
      />
    )
    : <Chip label={viewLabel || "Asset View"} size="small" variant="outlined" className="contributors-datagrid-drilldown-view-label" />

  return (
    <Box className="contributors-datagrid-drilldown-toolbar">
      <Stack
        direction="row"
        spacing={1}
        className="contributors-datagrid-drilldown-toolbar-left"
        sx={{
        alignItems: "center",
      }}
      >
        {viewSelector}
        <Typography variant="caption" className="contributors-datagrid-drilldown-date-label">{dateLabel}</Typography>
        {isIntraday && intradayAsOf && (
          <Typography variant="caption" sx={{ opacity: 0.6 }}>
            As of {moment(intradayAsOf, ["HH:mm:ssZ", moment.ISO_8601]).format("LTS")}
          </Typography>
        )}
        <FrozenContextTooltip
          frozenSnapshot={frozenSnapshot}
          selectedFactor={selectedFactor}
          selectedCategory={selectedCategory}
        />
      </Stack>
      <Stack
        direction="row"
        spacing={0.5}
        className="contributors-datagrid-drilldown-toolbar-right"
        sx={{
        alignItems: "center",
      }}
      >
        {isIntraday && (
          <>
            <Typography variant="caption" sx={{ opacity: 0.6, pr: 1 }}>
              {/* Once the refresh is armed the data is frozen at an earlier moment, so "Live
                  intraday pricing." would misstate it — staleness wins; the grid overlay still
                  explains the missing contributors in the batchless case. */}
              {hasNewerBatch
                ? "Showing an earlier intraday snapshot — refresh to load the latest."
                : intradayContributorsUnavailable
                  ? "Live intraday pricing."
                  : "Intraday drill-down is enabled for the latest available data."}
            </Typography>
            {hasNewerBatch && (
              <Tooltip title="Load latest intraday data">
                <IconButton size="small" onClick={onRefresh} aria-label="Load latest intraday data">
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Divider orientation="vertical" flexItem />
          </>
        )}
        <GroupingMenu
          setGrouping={onGroupByChange}
          currentGrouping={groupBy}
          availableRollups={availableGroupByOptions}
          hasMultipleInstruments={false}
        />
        <ExpansionMenu
          setExpansionMode={onExpansionToggle}
          isExpanded={isExpanded}
          hasUnexpandedComposites={false}
          isLoading={false}
        />
      </Stack>
    </Box>
  )
}

DrilldownToolbar.propTypes = {
  viewLabel: PropTypes.string,
  currentFactor: PropTypes.string,
  factorOptions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired, primaryText: PropTypes.string.isRequired })),
  onFactorChange: PropTypes.func,
  frozenSnapshot: PropTypes.object,
  selectedFactor: PropTypes.string,
  selectedCategory: PropTypes.string,
  isIntraday: PropTypes.bool,
  intradayAsOf: PropTypes.string,
  hasNewerBatch: PropTypes.bool,
  intradayContributorsUnavailable: PropTypes.bool,
  onRefresh: PropTypes.func,
  groupBy: PropTypes.object,
  onGroupByChange: PropTypes.func,
  availableGroupByOptions: PropTypes.arrayOf(PropTypes.object),
  isExpanded: PropTypes.bool,
  onExpansionToggle: PropTypes.func,
}
